import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiService } from '@app/core/api.service';
import { ServiceArea } from '@app/shared/service-area';
import { ServiceAreaSuggestionGraphQL } from '@app/shared/service-area-suggestion-graphql.service';

export interface ServiceAreaPediatricsResponse {
  pediatrics_available: boolean;
}

export const publicServiceAreaPath = '/api/v2/public/service_areas';

@Injectable()
export class ServiceAreaService {
  private _serviceAreas$ = new Subject<Array<ServiceArea>>();
  readonly serviceAreas$ = this._serviceAreas$.asObservable();

  private _suggestedServiceArea$ = new Subject<ServiceArea | null>();
  readonly suggestedServiceArea$ = this._suggestedServiceArea$.asObservable();

  /**
   * Service Area filtered
   */
  readonly nonVirtualServiceAreas$ = this.serviceAreas$.pipe(
    map(allServiceAreas => allServiceAreas.filter(serviceArea => !serviceArea.virtual)),
  );

  readonly virtualServiceAreas$ = this.serviceAreas$.pipe(
    map(allServiceAreas => allServiceAreas.filter(serviceArea => !!serviceArea.virtual)),
  );

  /**
   * Service Areas formatted for selection input options
   */
  readonly allServiceAreaOptions$ = this.serviceAreas$;

  readonly nonVirtualServiceAreaOptions$ = this.nonVirtualServiceAreas$;

  private _pediatricsAvailableInServiceArea$ = new Subject<boolean>();
  readonly pediatricsAvailableInServiceArea$ = this._pediatricsAvailableInServiceArea$.asObservable();

  constructor(private apiService: ApiService, private serviceAreaSuggestionGraphQL: ServiceAreaSuggestionGraphQL) {}

  getServiceAreas(b2bCompanyId?: number): Observable<Array<ServiceArea>> {
    const serviceAreaPath = b2bCompanyId
      ? `/api/v2/public/b2b_companies/${b2bCompanyId}/service_areas`
      : publicServiceAreaPath;

    this.apiService
      .get<Record<string, any>[]>(serviceAreaPath)
      .pipe(
        map(rawServiceAreas => rawServiceAreas.map(sa => ServiceArea.fromApiV2(sa))),
        map((serviceAreas): ServiceArea[] =>
          serviceAreas
            .filter(serviceArea => !serviceArea.registration_hidden)
            // Sort alphabetically and set the virtual service area last
            .sort((a, b) => (a.virtual === b.virtual ? 0 : a.virtual ? 1 : -1) || a.name.localeCompare(b.name)),
        ),
      )
      .subscribe(serviceAreas => this._serviceAreas$.next(serviceAreas));

    return this.serviceAreas$;
  }

  getServiceAreaByName(name: string) {
    return this.serviceAreas$.pipe(map(x => x.find(y => y.name === name)));
  }

  getPediatricsAvailability(serviceArea: ServiceArea): void {
    const pedsAvailablePath = `${publicServiceAreaPath}/${serviceArea.id}/pediatrics_available`;
    this.apiService.get(pedsAvailablePath).subscribe((response: ServiceAreaPediatricsResponse) => {
      this._pediatricsAvailableInServiceArea$.next(response.pediatrics_available);
    });
  }

  getSuggestedServiceArea(): Observable<ServiceArea | null> {
    this.serviceAreaSuggestionGraphQL
      .fetch()
      .pipe(
        map(result => {
          const suggestedServiceArea = result.data?.suggestedServiceArea?.serviceArea;
          return suggestedServiceArea ? ServiceArea.fromGraphQL(suggestedServiceArea) : null;
        }),
      )
      .subscribe(serviceArea => this._suggestedServiceArea$.next(serviceArea));

    return this.suggestedServiceArea$;
  }
}
